import { axiosInstance, axiosPrivate } from "~/config/axios.config";
import { logInfo } from "~/lib/logger";
import { handleApiError, extractData } from "~/lib/utils";
import type {
  ApiResponse,
  AuthResponse,
  LoginResult,
  MfaChallengeRequest,
  MfaSetupBeginResponse,
  MfaSetupCompleteResponse,
  CreateAccountResponse,
  UserProfile,
  RefreshSessionRequest,
  RefreshSessionResponse,
  LoginRequest,
  AcceptInviteTokenInfo,
  AcceptInviteRequest,
  DisableAccountRequest,
  OrgUsersResponse,
  OrgUsersParams,
  MagicLinkConsumeResponse,
  ImpersonationHandoffResponse,
} from "~/types";
export type CreateAccountRequest = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // Optional marketing pricing-card deep-link intent (?plan=&cadence=), forwarded
  // to the backend which persists it as the org's intended plan (XD-025). Raw
  // slugs (essentials/plus/enterprise, monthly/annual); unknown values are
  // ignored server-side, never rejected.
  plan?: string;
  cadence?: string;
};

/** Client timeout for POST /v1/auth/refresh — see refreshSession. */
export const REFRESH_REQUEST_TIMEOUT_MS = 10_000;

/** Client timeout for POST /v1/auth/logout — see logout. */
export const LOGOUT_REQUEST_TIMEOUT_MS = 10_000;

class AuthService {
  /**
   * Create account
   * Note: Create account response does not include auth tokens (access/refresh)
   * User needs to verify email before logging in
   */
  static async createAccount(
    credentials: CreateAccountRequest,
  ): Promise<ApiResponse<CreateAccountResponse>> {
    const url = "/v1/auth/signup";

    // Prepare payload for API request (exclude confirmPassword)
    const createAccountPayload = {
      email: credentials.email,
      password: credentials.password,
      firstName: credentials.firstName,
      lastName: credentials.lastName,
      // Only send the deep-link intent when present, so a plain signup posts
      // the same four-field body it always did.
      ...(credentials.plan ? { plan: credentials.plan } : {}),
      ...(credentials.cadence ? { cadence: credentials.cadence } : {}),
    };

    // Prepare payload for logging (mask password for security)
    const logPayload = {
      email: credentials.email,
      password: "***", // Masked for security
      firstName: credentials.firstName,
      lastName: credentials.lastName,
    };

    // Log: Create account request payload with email and masked password
    logInfo("Create account API request payload:", {
      data: logPayload,
    });

    try {
      const response = await axiosInstance.post<
        ApiResponse<CreateAccountResponse>
      >(url, createAccountPayload);

      logInfo("Create account API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return response.data;
    } catch (error: unknown) {
      handleApiError(error, "Failed to create account", {
        url,
        method: "POST",
        errorType: "create_account_error",
      });
    }
  }

  /**
   * User login
   */
  static async login(credentials: LoginRequest): Promise<LoginResult> {
    const url = "/v1/auth/login";

    // Prepare payload for logging (mask password for security)
    const logPayload = {
      email: credentials.email,
      password: "***", // Masked for security
      rememberMe: credentials.rememberMe,
      // Never log the token itself — only whether one was sent.
      hasCaptchaToken: Boolean(credentials.captchaToken),
    };

    // Log: Login request payload with email and masked password
    logInfo("Login API request payload:", {
      data: logPayload,
    });

    try {
      const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
        url,
        {
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe,
          // Only once the API has asked for one (after 3 failures on this email).
          ...(credentials.captchaToken
            ? { captchaToken: credentials.captchaToken }
            : {}),
        },
        {
          withCredentials: true,
        },
      );

      logInfo("Login API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return extractData(response) as LoginResult;
    } catch (error: unknown) {
      handleApiError(error, "Failed to login", {
        url,
        method: "POST",
        errorType: "login_error",
      });
    }
  }

  /**
   * DEV-FA2 — complete an MFA-gated login. Consumes the mfaToken from login and
   * a TOTP or backup code; on success the backend issues the session
   * (palate_session + palate_csrf cookies) and returns the normal login payload,
   * so the caller treats this exactly like a successful login.
   */
  static async mfaChallenge(payload: MfaChallengeRequest): Promise<AuthResponse> {
    const url = "/v1/auth/mfa/challenge";
    logInfo("MFA challenge request", { data: { mfaToken: "***", code: "***" } });
    try {
      const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
        url,
        payload,
        { withCredentials: true },
      );
      return extractData(response) as AuthResponse;
    } catch (error: unknown) {
      handleApiError(error, "Failed to verify code", {
        url,
        method: "POST",
        errorType: "mfa_challenge_error",
      });
    }
  }

  /**
   * DEV-FC — initiate forced MFA enrolment. Validates the setupToken (15-min TTL)
   * without consuming it, so the caller can call it again to re-render the QR.
   * Returns the QR data URI, the manual secret, and the otpauth URI.
   *
   * 401 with { code: 'AUTH_MFA_TOKEN_INVALID' } if the token has expired.
   */
  static async mfaSetupBegin(setupToken: string): Promise<MfaSetupBeginResponse> {
    const url = "/v1/auth/mfa/setup-begin";
    logInfo("MFA setup-begin request", { data: { setupToken: "***" } });
    try {
      const response = await axiosInstance.post<ApiResponse<MfaSetupBeginResponse>>(
        url,
        { setupToken },
        { withCredentials: true },
      );
      return extractData(response) as MfaSetupBeginResponse;
    } catch (error: unknown) {
      handleApiError(error, "Failed to begin MFA setup", {
        url,
        method: "POST",
        errorType: "mfa_setup_begin_error",
      });
    }
  }

  /**
   * DEV-FC — complete forced MFA enrolment. Verifies the TOTP code and, on
   * success, issues the session cookies (palate_session, palate_csrf,
   * refresh_token) and returns the normal login payload PLUS backup codes.
   *
   * Retryable error: 401 with message "Invalid verification code." (setupToken
   * survives — let the user re-enter the code).
   * Terminal errors (both 401):
   *   { code: 'AUTH_MFA_SETUP_ATTEMPTS_EXCEEDED' } — must restart from /login.
   *   { code: 'AUTH_MFA_TOKEN_INVALID' } — token expired, must restart.
   */
  static async mfaSetupComplete(
    setupToken: string,
    code: string,
  ): Promise<MfaSetupCompleteResponse> {
    const url = "/v1/auth/mfa/setup-complete";
    logInfo("MFA setup-complete request", { data: { setupToken: "***", code: "***" } });
    try {
      const response = await axiosInstance.post<ApiResponse<MfaSetupCompleteResponse>>(
        url,
        { setupToken, code },
        { withCredentials: true },
      );
      return extractData(response) as MfaSetupCompleteResponse;
    } catch (error: unknown) {
      handleApiError(error, "Failed to complete MFA setup", {
        url,
        method: "POST",
        errorType: "mfa_setup_complete_error",
      });
    }
  }

  /**
   * DEV-FA4 — re-confirm the account password to elevate the current session to
   * sudo (~15 min). Authenticates via the session cookie (SessionGuard), so it
   * relies on `withCredentials`. A wrong password throws with statusCode 401 /
   * errorCode "invalid_credentials"; the caller surfaces error.message.
   *
   * A user with 2FA may prove it with a current 6-digit authenticator code
   * instead (QA-SU4): pass `{ code }`. The API takes the password OR the code,
   * never both. A bare string is the password, as every caller before had it.
   */
  static async elevateSudo(
    proof: string | { password: string } | { code: string },
  ): Promise<{ sudoElevated: boolean; expiresInSeconds: number }> {
    const url = "/v1/auth/sudo";
    const body = typeof proof === "string" ? { password: proof } : proof;
    logInfo("Sudo elevation request", {
      data: { method: "code" in body ? "authenticator code" : "password" },
    });
    try {
      const response = await axiosPrivate.post<
        ApiResponse<{ sudoElevated: boolean; expiresInSeconds: number }>
      >(url, body, { withCredentials: true });
      return extractData(response) as {
        sudoElevated: boolean;
        expiresInSeconds: number;
      };
    } catch (error: unknown) {
      handleApiError(error, "Confirmation failed", {
        url,
        method: "POST",
        errorType: "sudo_error",
      });
    }
  }

  /**
   * DEV-FA5 — live "has this password been breached?" check for the
   * password-strength UI. Public endpoint (used pre-auth at signup). Fails open:
   * any error returns false so a breach-service hiccup never blocks the user;
   * the backend re-checks at submit, so a false "not pwned" here is safe.
   */
  static async checkPasswordPwned(password: string): Promise<boolean> {
    const url = "/v1/auth/check-password-pwned";
    try {
      const response = await axiosInstance.post<
        ApiResponse<{ pwned: boolean }>
      >(url, { password });
      return (extractData(response) as { pwned: boolean }).pwned;
    } catch {
      return false; // fail open — never block on a breach-service hiccup
    }
  }

  /**
   * Get current user profile
   * Requires authentication token
   * @returns User profile with company/organization details
   */
  static async getMe(): Promise<UserProfile> {
    const url = "/v1/auth/me";

    logInfo("Get me API request:", {
      data: { url },
    });

    try {
      const response = await axiosPrivate.get<ApiResponse<UserProfile>>(url);

      logInfo("Get me API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      const data = extractData(response);
      if (data) {
        return data;
      }

      throw new Error("Invalid response structure from getMe endpoint");
    } catch (error: unknown) {
      handleApiError(error, "Failed to fetch profile", {
        url,
        method: "GET",
        errorType: "get_me_error",
      });
    }
  }

  /**
   * OPS-3.5 — redeem the single-use handoff code the Ops Panel put in the URL
   * (`?impersonation_code=`) for the impersonation token. Public (the tab
   * holds no credential yet — the code is the credential), so it goes out on
   * axiosInstance. Works once: a replay answers 410
   * IMPERSONATION_HANDOFF_INVALID, surfaced on the thrown error's
   * `statusCode` / `errorCode`.
   *
   * Neither the code nor the response is logged — both are credentials.
   */
  static async redeemImpersonationHandoff(
    code: string,
  ): Promise<ImpersonationHandoffResponse> {
    const url = "/internal/impersonation/handoff";
    try {
      const response = await axiosInstance.post<
        ApiResponse<ImpersonationHandoffResponse>
      >(url, { code });
      const data = extractData(response);
      if (data?.accessToken) return data;
      throw new Error("Invalid response structure from impersonation handoff");
    } catch (error: unknown) {
      handleApiError(error, "Couldn't start the support session", {
        url,
        method: "POST",
        errorType: "impersonation_handoff_error",
      });
    }
  }

  /**
   * Refresh authentication session with refresh token
   * Uses axiosInstance (public) to avoid interceptor conflicts
   */
  static async refreshSession(
    data: RefreshSessionRequest = {},
  ): Promise<RefreshSessionResponse> {
    const url = "/v1/auth/refresh";

    logInfo("Refresh session API request (Cookie-based):", {
      data: {
        hasRefreshTokenInBody: !!data.refreshToken,
        method: "POST",
      },
    });

    try {
      // Send refresh token in body (cookie may not be sent from localhost/staging due to Domain)
      const body =
        data.refreshToken != null
          ? { refreshToken: data.refreshToken }
          : {};

      const response = await axiosInstance.post<ApiResponse<RefreshSessionResponse>>(
        url,
        body,
        {
          withCredentials: true, // Send HttpOnly cookie when available
          // Much shorter than the instance's 45s: a refresh that times out
          // may still have rotated server-side, and its retry only gets the
          // new pair back through backend#1854's 45s grace window measured
          // from that rotation (axios.config.ts, "Transient refresh retry").
          // At 45s every retry would land past it.
          timeout: REFRESH_REQUEST_TIMEOUT_MS,
        },
      );

      const result = extractData(response) as RefreshSessionResponse;

      logInfo("Refresh session API response:", {
        data: { hasAccessToken: !!result?.accessToken },
      });

      return result;
    } catch (error: unknown) {
      handleApiError(error, "Failed to refresh session", {
        url,
        method: "POST",
        errorType: "refresh_session_error",
      });
    }
  }

  /**
   * End a session server-side (POST /v1/auth/logout).
   *
   * #931 — sent through the PUBLIC instance with the credential passed in,
   * never through axiosPrivate: that instance's 401 interceptor would answer
   * a refused logout by refreshing the session off the HttpOnly refresh
   * cookie — minting a new session in the middle of ending one — and it reads
   * the token from the auth store, which sign-out has already cleared by the
   * time a retry runs.
   *
   * The backend (LogoutAuthGuard, backend#1076) authenticates EITHER a Bearer
   * access token OR the `palate_session` cookie (CSRF-echoed — axiosInstance
   * attaches X-CSRF-Token). It does not accept the refresh cookie. Sending
   * the access token even when it has lapsed matters: on the cookie path the
   * backend then also revokes the JWT session it names, and with it the
   * refresh token (`revokeLapsedAccessToken`). 401 means neither credential
   * authenticated.
   */
  static async logout(
    options: { accessToken?: string | null } = {},
  ): Promise<{ message?: string }> {
    const url = "/v1/auth/logout";

    logInfo("Logout API request:", {
      data: { url, hasAccessToken: !!options.accessToken },
    });

    try {
      const response = await axiosInstance.post<
        ApiResponse<{ message?: string }>
      >(
        url,
        {},
        {
          headers: options.accessToken
            ? { Authorization: `Bearer ${options.accessToken}` }
            : undefined,
          // Short, like refresh: sign-out retries in the background, and the
          // unload path cannot wait for the instance's 45s either.
          timeout: LOGOUT_REQUEST_TIMEOUT_MS,
        },
      );

      logInfo("Logout API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return (
        extractData(response) || {
          message: response.data?.message || "Logged out successfully",
        }
      );
    } catch (error: unknown) {
      handleApiError(error, "Failed to logout", {
        url,
        method: "POST",
        errorType: "logout_error",
      });
    }
  }

  // logoutAll (POST /v1/auth/logout-all) removed — route never existed on the
  // backend. Use revokeAllSessions (DELETE /v1/settings/sessions) instead,
  // via useRevokeAllSessions in app/hooks/settings/account.ts.

  /**
   * Verify email (sends OTP to email) TODO:will be updated
   */
  // static async verifyEmail(email: string) {
  //   const url = "/v1/auth/verify-email"; // TODO:will be updated

  //   logInfo("Verify email API request payload:", {
  //     data: { email },
  //   });

  //   try {
  //     const response = await axiosInstance.post(url, { email });

  //     logInfo("Verify email API response:", {
  //       data: response.data as unknown as Record<string, unknown>,
  //     });

  //     return response.data;
  //   } catch (error: unknown) {
  //     logError("Verify email API error:", error);
  //     throw error;
  //   }
  // }

  /**
   * Forgot password (sends OTP or reset link to email)
   */
  static async forgotPassword(data: {
    email: string;
  }): Promise<{ message?: string }> {
    const url = "/v1/auth/forgot-password";

    logInfo("Forgot password API request payload:", {
      data: { email: data.email },
    });

    try {
      const response = await axiosInstance.post<
        ApiResponse<{ message?: string }>
      >(url, data);

      logInfo("Forgot password API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return (
        extractData(response) || {
          message: response.data?.message || "Reset link sent successfully",
        }
      );
    } catch (error: unknown) {
      handleApiError(error, "Failed to send reset link", {
        url,
        method: "POST",
        errorType: "forgot_password_error",
      });
    }
  }

  /**
   * Reset password (with token from email link)
   */
  static async resetPassword(data: {
    token: string;
    password: string;
  }): Promise<{ message?: string }> {
    const url = "/v1/auth/reset-password";

    logInfo("Reset password API request:", {
      data: { hasToken: !!data.token },
    });

    try {
      const response = await axiosInstance.post<
        ApiResponse<{ message?: string }>
      >(url, {
        token: data.token,
        password: data.password, // Masked in logInfo but passed here
      });

      logInfo("Reset password API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return (
        extractData(response) || {
          message: response.data?.message || "Password reset successfully",
        }
      );
    } catch (error: unknown) {
      handleApiError(error, "Failed to reset password", {
        url,
        method: "POST",
        errorType: "reset_password_error",
      });
    }
  }
  /**
   * Validate an invite token and get employee info
   */
  static async validateInviteToken(
    token: string,
  ): Promise<AcceptInviteTokenInfo> {
    const url = `/v1/auth/invite/${token}`;

    logInfo("Validate invite token API request:", {
      data: { hasToken: !!token },
    });

    try {
      const response = await axiosInstance.get<
        ApiResponse<AcceptInviteTokenInfo>
      >(url);

      logInfo("Validate invite token API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return extractData(response) as AcceptInviteTokenInfo;
    } catch (error: unknown) {
      handleApiError(error, "Failed to validate invite token", {
        url,
        method: "GET",
        errorType: "validate_invite_error",
      });
    }
  }

  /**
   * TC-9.1.14 (Sprint 9 round-4) — consume a magic link (email-change
   * confirmation, account claim, MFA recovery — see MagicLinkConsumeResponse).
   *
   * Public route — the single-use token is the credential; no auth header.
   * The backend also sets a session cookie as a side effect of consuming the
   * link (see palate-admin-backend AuthController.consumeMagicLink), but this
   * admin app's own auth flow (Bearer JWT in useAuthStore, refreshed via
   * axiosPrivate) is unaffected either way — this call only needs the JSON
   * body to confirm what happened and render the right page state.
   *
   * Errors: 404 "This link is invalid." (unknown token) or 410 "This link
   * has already been used." / "This link has expired." (consumed/expired
   * token) — both terminal, non-retryable states for the page to show.
   */
  static async consumeMagicLink(
    token: string,
  ): Promise<MagicLinkConsumeResponse> {
    const url = `/v1/auth/m/${token}`;

    logInfo("Consume magic link API request:", {
      data: { hasToken: !!token },
    });

    try {
      const response = await axiosInstance.get<
        ApiResponse<MagicLinkConsumeResponse>
      >(url);

      logInfo("Consume magic link API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return extractData(response) as MagicLinkConsumeResponse;
    } catch (error: unknown) {
      handleApiError(error, "This link is invalid or has expired.", {
        url,
        method: "GET",
        errorType: "consume_magic_link_error",
      });
    }
  }

  /**
   * Complete an admin-initiated employee PIN reset (TC-8.1.9 / TC-8.1.10).
   *
   * The employee arrives via the email deep-link
   * `{FRONTEND_URL}/employee/reset-pin?token=<token>` and submits a new PIN.
   * Public route — the reset token (single-use, 30-min TTL) is the credential;
   * no auth header. On success the backend revokes all the employee's sessions
   * so the old PIN stops working immediately.
   *
   * Errors (all 400):
   *  - expired / used / unknown token → "Invalid or expired reset token"
   *  - sequential PIN (1234) / repeated digits (1111) / not 4–6 digits → policy message
   */
  static async resetEmployeePin(data: {
    resetToken: string;
    newPin: string;
  }): Promise<{ message?: string }> {
    const url = "/v1/auth/employee/reset-pin";

    logInfo("Reset employee PIN API request:", {
      data: { hasToken: !!data.resetToken },
    });

    try {
      const response = await axiosInstance.post<
        ApiResponse<{ message?: string }>
      >(url, { resetToken: data.resetToken, newPin: data.newPin });

      logInfo("Reset employee PIN API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return (
        extractData(response) || {
          message: response.data?.message || "PIN has been reset successfully",
        }
      );
    } catch (error: unknown) {
      handleApiError(error, "Failed to reset PIN", {
        url,
        method: "POST",
        errorType: "reset_employee_pin_error",
      });
    }
  }

  /**
   * Accept invite — set password and activate account
   */
  static async acceptInvite(
    data: AcceptInviteRequest,
  ): Promise<AuthResponse> {
    const url = "/v1/auth/invite/accept";

    logInfo("Accept invite API request:", {
      data: { hasToken: !!data.token },
    });

    try {
      const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
        url,
        { token: data.token, password: data.password },
      );

      logInfo("Accept invite API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return extractData(response) as AuthResponse;
    } catch (error: unknown) {
      handleApiError(error, "Failed to accept invite", {
        url,
        method: "POST",
        errorType: "accept_invite_error",
      });
    }
  }
  /**
   * Disable a user account (OWNER/ADMIN only)
   * Revokes all sessions for the target user
   */
  static async disableAccount(
    data: DisableAccountRequest,
  ): Promise<{ message: string }> {
    const url = "/v1/auth/disable-account";

    logInfo("Disable account API request:", {
      data: { email: data.email },
    });

    try {
      const response = await axiosPrivate.post<
        ApiResponse<{ message: string }>
      >(url, data);

      logInfo("Disable account API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return (
        extractData(response) || {
          message: response.data?.message || "Account disabled successfully",
        }
      );
    } catch (error: unknown) {
      handleApiError(error, "Failed to disable account", {
        url,
        method: "POST",
        errorType: "disable_account_error",
        extraData: { email: data.email },
      });
    }
  }

  /**
   * List users in an organization (OWNER/ADMIN only)
   * Supports pagination and status filtering
   */
  static async getOrganizationUsers(
    params: OrgUsersParams,
  ): Promise<OrgUsersResponse> {
    const { organizationId, page = 1, limit = 20, status } = params;
    const url = `/v1/auth/organizations/${organizationId}/users`;

    logInfo("Get organization users API request:", {
      data: { organizationId, page, limit, status },
    });

    try {
      const response = await axiosPrivate.get<ApiResponse<OrgUsersResponse>>(
        url,
        { params: { page, limit, ...(status && { status }) } },
      );

      logInfo("Get organization users API response:", {
        data: response.data as unknown as Record<string, unknown>,
      });

      return extractData(response) as OrgUsersResponse;
    } catch (error: unknown) {
      handleApiError(error, "Failed to fetch organization users", {
        url,
        method: "GET",
        errorType: "get_org_users_error",
        extraData: { organizationId },
      });
    }
  }
}

export default AuthService;
